import { useEffect, useMemo, useState } from "react";
import type { AgentFlowDTO, AnalyticsResponse, AnalyticsWindowDays } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";

/**
 * AgentFlow analytics dashboard.
 *
 * Reads `GET /v1/agentflow/analytics?windowDays=...`. Plain HTML/SVG
 * charts — no recharts dep in this module to keep the bundle small.
 * The visual cadence mirrors the `/insights/*` pages so the AgentFlow
 * dashboard feels consistent with the rest of the app.
 */

type State =
  | { status: "loading" }
  | { status: "ready"; data: AnalyticsResponse }
  | { status: "error"; message: string };

export function AnalyticsPage() {
  const [windowDays, setWindowDays] = useState<AnalyticsWindowDays>(7);
  const [flowId, setFlowId] = useState<string>("");
  const [flows, setFlows] = useState<AgentFlowDTO[] | null>(null);
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    void api.agentflow
      .listFlows()
      .then((r) => setFlows(r.flows))
      .catch(() => setFlows([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    api.agentflow
      .analytics(windowDays, flowId === "" ? undefined : flowId)
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: e instanceof ApiClientError ? e.message : "Failed to load analytics",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays, flowId]);

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Analytics
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          AgentFlow analytics
        </h1>
        <p className="mt-1 max-w-2xl text-[13.5px] text-zinc-600 dark:text-zinc-400">
          Per-conversation rollups for live and sandbox traffic. Updated synchronously at each
          turn, cached for 30 seconds.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <WindowSelector value={windowDays} onChange={setWindowDays} />
        {flows && flows.length > 0 && (
          <select
            value={flowId}
            onChange={(e) => setFlowId(e.target.value)}
            className="h-9 rounded-md border border-zinc-200 bg-white px-3 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
          >
            <option value="">All flows</option>
            {flows.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {state.status === "loading" && <SkeletonGrid />}
      {state.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </p>
      )}
      {state.status === "ready" && <Dashboard data={state.data} />}
    </div>
  );
}

function WindowSelector({
  value,
  onChange,
}: {
  value: AnalyticsWindowDays;
  onChange: (n: AnalyticsWindowDays) => void;
}) {
  const opts: Array<{ value: AnalyticsWindowDays; label: string }> = [
    { value: 1, label: "24h" },
    { value: 7, label: "7d" },
    { value: 30, label: "30d" },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Time window"
      className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={
            "rounded-[5px] px-3 py-1 text-[12px] font-medium transition " +
            (value === o.value
              ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-50"
              : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Dashboard({ data }: { data: AnalyticsResponse }) {
  const cards = useMemo(() => buildCards(data), [data]);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label} label={c.label} value={c.value} hint={c.hint} />
        ))}
      </div>
      <section>
        <h2 className="mb-2 text-[13px] font-semibold text-zinc-800 dark:text-zinc-200">
          Conversations per day
        </h2>
        <BarChart points={data.timeseries.map((p) => ({ x: p.day, y: p.conversations }))} />
      </section>
      <section>
        <h2 className="mb-2 text-[13px] font-semibold text-zinc-800 dark:text-zinc-200">
          Top tools
        </h2>
        {data.topTools.length === 0 && (
          <p className="text-[12.5px] text-zinc-500">No tool calls in this window.</p>
        )}
        {data.topTools.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
              <tr>
                <th className="px-3 py-2">Tool</th>
                <th className="px-3 py-2">Calls</th>
                <th className="px-3 py-2">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
              {data.topTools.map((t) => (
                <tr key={t.toolId}>
                  <td className="px-3 py-2 font-mono text-[13px]">{t.toolId}</td>
                  <td className="px-3 py-2">{t.count}</td>
                  <td className="px-3 py-2">{Math.round(t.share * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function buildCards(data: AnalyticsResponse) {
  const { overview } = data;
  return [
    {
      label: "Conversations",
      value: overview.conversations.toLocaleString("en-US"),
      hint: `${overview.windowDays}d window`,
    },
    {
      label: "Completion rate",
      value: overview.completionRate == null ? "—" : `${Math.round(overview.completionRate * 100)}%`,
      hint: `${overview.completed} of ${overview.conversations} completed`,
    },
    {
      label: "Avg turns / conv",
      value: overview.avgTurns == null ? "—" : overview.avgTurns.toFixed(2),
      hint: `${overview.toolCalls} tool calls`,
    },
    {
      label: "Cost (USD)",
      value: `$${overview.totalCostUsd.toFixed(2)}`,
      hint: "Sum of per-turn estimates",
    },
  ];
}

function Card({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800/80 dark:bg-zinc-900/40">
      <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-zinc-900 dark:text-zinc-50">{value}</p>
      <p className="mt-0.5 text-[11.5px] text-zinc-500">{hint}</p>
    </div>
  );
}

function BarChart({ points }: { points: Array<{ x: string; y: number }> }) {
  if (points.length === 0) {
    return (
      <p className="text-[12.5px] text-zinc-500">No conversations in this window.</p>
    );
  }
  const max = Math.max(...points.map((p) => p.y), 1);
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white p-3 dark:border-zinc-800/80 dark:bg-zinc-900/40">
      <div className="flex items-end gap-1" style={{ height: 140 }}>
        {points.map((p) => (
          <div
            key={p.x}
            className="flex flex-1 flex-col items-center justify-end gap-1"
            title={`${p.x}: ${p.y}`}
          >
            <div
              className="w-full rounded-t bg-brand-500/70 transition hover:bg-brand-500"
              style={{ height: `${(p.y / max) * 120}px`, minHeight: 2 }}
            />
            <span className="text-[9.5px] text-zinc-500">{p.x.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="h-20 animate-pulse rounded-xl border border-zinc-200 bg-zinc-100 dark:border-zinc-800/80 dark:bg-zinc-800/30"
        />
      ))}
    </div>
  );
}
